import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Divider,
  Checkbox,
  FormControlLabel,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  NavigateNext as GoIcon,
  AccessTime as ClockIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import timeClockService, { PunchStatus, PunchType, StaffOption } from '../services/timeClockService';

// ── Types ────────────────────────────────────────────────────────────────────

interface ReadyToShipCounts {
  shopee: number;
  shopify: number;
  storefront: number;
  total: number;
}

interface DailyTask {
  id: string;
  label: string;
  priority: 'must' | 'optional';
  done: boolean;
}

// ── Default daily tasks ──────────────────────────────────────────────────────

const DEFAULT_TASKS: Omit<DailyTask, 'done'>[] = [
  // 必做
  { id: 'ship_orders', label: '出貨（寄出所有可出貨訂單）', priority: 'must' },
  { id: 'verify_shipped', label: '檢查有無漏寄漏刷', priority: 'must' },
  { id: 'no_cvs_in_home', label: '超商的不要放宅配', priority: 'must' },
  { id: 'mdoc_orders', label: 'MdoC / DoingWhat 訂單出貨', priority: 'must' },
  { id: 'pickup_returns', label: '寄貨時領退貨（直接報末三碼）', priority: 'must' },
  { id: 'trash', label: '倒垃圾', priority: 'must' },
  // 有空可做
  { id: 'labels', label: '貼標籤', priority: 'optional' },
  { id: 'returns', label: '領退貨 / 拆退貨', priority: 'optional' },
  { id: 'shelving', label: '組層架', priority: 'optional' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const todayKey = () => new Date().toISOString().slice(0, 10); // YYYY-MM-DD

function loadChecklist(): DailyTask[] {
  try {
    const key = `dashboard_tasks_${todayKey()}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed: Record<string, boolean> = JSON.parse(saved);
      return DEFAULT_TASKS.map(t => ({ ...t, done: !!parsed[t.id] }));
    }
  } catch { /* ignore */ }
  return DEFAULT_TASKS.map(t => ({ ...t, done: false }));
}

function saveChecklist(tasks: DailyTask[]) {
  const key = `dashboard_tasks_${todayKey()}`;
  const map: Record<string, boolean> = {};
  tasks.forEach(t => { map[t.id] = t.done; });
  localStorage.setItem(key, JSON.stringify(map));
}

// ── Component ────────────────────────────────────────────────────────────────

const DashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState<ReadyToShipCounts>({ shopee: 0, shopify: 0, storefront: 0, total: 0 });
  const [tasks, setTasks] = useState<DailyTask[]>(loadChecklist);

  const { shopeeAuthenticated } = useAuth();
  const navigate = useNavigate();

  // ── Fetch 可出貨 counts ──────────────────────────────────────────────────

  const fetchReadyToShip = useCallback(async () => {
    setLoading(true);
    try {
      // Same API calls as receiving page — fetch unfulfilled orders + tracking
      const [shopeeRes, shopifyRes, sfRes, shopeeTrackRes, shopifyTrackRes, sfTrackRes] =
        await Promise.allSettled([
          shopeeAuthenticated
            ? api.get('/api/shopee/orders').then(async (r) => {
                const orderList = r.data.response?.order_list || [];
                if (orderList.length === 0) return { details: [] };
                const sns = orderList.map((o: any) => o.order_sn);
                const detailsRes = await api.get(`/api/shopee/orders/details?orderSnList=${sns.join(',')}`);
                return { details: detailsRes.data.response?.order_list || [] };
              })
            : Promise.resolve({ details: [] }),
          api.get('/api/shopify/orders', { params: { limit: 600, fulfillmentFilter: 'unfulfilled' } }),
          api.get('/api/storefront/orders', { params: { limit: 1000, status: 'unshipped' } }),
          api.get('/api/orders/tracking').catch(() => ({ data: [] })),
          api.get('/api/orders/shopify-tracking').catch(() => ({ data: [] })),
          api.get('/api/storefront/orders-tracking').catch(() => ({ data: [] })),
        ]);

      // Extract raw data
      const shopeeDetails: any[] = shopeeRes.status === 'fulfilled' ? (shopeeRes.value as any).details || [] : [];
      const shopifyOrders: any[] = shopifyRes.status === 'fulfilled' ? (shopifyRes.value as any).data?.orders || [] : [];
      const sfOrders: any[] = sfRes.status === 'fulfilled' ? (sfRes.value as any).data?.orders || [] : [];
      const shopeeTracking: any[] = shopeeTrackRes.status === 'fulfilled' ? (shopeeTrackRes.value as any).data || [] : [];
      const shopifyTracking: any[] = shopifyTrackRes.status === 'fulfilled' ? (shopifyTrackRes.value as any).data || [] : [];
      const sfTracking: any[] = sfTrackRes.status === 'fulfilled' ? (sfTrackRes.value as any).data || [] : [];

      // Count ready-to-ship for each platform
      const shopeeReady = countReady(shopeeDetails, shopeeTracking, 'shopee');
      const shopifyReady = countReady(shopifyOrders, shopifyTracking, 'shopify');
      const sfReady = countReady(sfOrders, sfTracking, 'storefront');

      setCounts({
        shopee: shopeeReady,
        shopify: shopifyReady,
        storefront: sfReady,
        total: shopeeReady + shopifyReady + sfReady,
      });
    } catch (err) {
      console.error('Failed to fetch ready-to-ship counts:', err);
    } finally {
      setLoading(false);
    }
  }, [shopeeAuthenticated]);

  useEffect(() => { fetchReadyToShip(); }, [fetchReadyToShip]);

  // ── Count ready-to-ship orders per platform ────────────────────────────

  function countReady(orders: any[], tracking: any[], platform: string): number {
    if (!orders.length) return 0;

    // Build tracking lookup: key → { received, ... }
    const trackMap = new Map<string, any>();
    tracking.forEach((t: any) => {
      const key = platform === 'shopee'
        ? `${t.order_sn || t.orderSn}_${t.product_name || t.productName}_${t.item_spec || t.itemSpec || ''}`
        : platform === 'shopify'
        ? `${t.order_id || t.orderId}_${t.product_name || t.productName}_${t.variant_title || t.variantTitle || ''}`
        : `${t.order_id || t.orderId}_${t.product_name || t.productName}_${t.variant_title || t.variantTitle || ''}`;
      trackMap.set(key, t);
    });

    let readyCount = 0;

    if (platform === 'shopee') {
      // Only READY_TO_SHIP — PROCESSED means already 出貨了
      const eligible = orders.filter((o: any) =>
        o.order_status === 'READY_TO_SHIP'
      );
      for (const order of eligible) {
        const items = order.item_list || [];
        if (items.length === 0) continue;
        const allReceived = items.every((item: any) => {
          const spec = (item.model_name || '').trim();
          const key = `${order.order_sn}_${item.item_name}_${spec}`;
          const t = trackMap.get(key);
          return t?.received;
        });
        // Also check it doesn't already have tracking
        const hasTracking = !!(order.tracking_number || order.package_list?.[0]?.tracking_number);
        if (allReceived && !hasTracking) readyCount++;
      }
    } else if (platform === 'shopify') {
      const eligible = orders.filter((o: any) =>
        o.fulfillment_status !== 'fulfilled' && !o.is_shipped
      );
      for (const order of eligible) {
        const items = order.line_items || order.items || [];
        if (items.length === 0) continue;
        const allReceived = items.every((item: any) => {
          const variant = item.variant_title || '';
          const orderId = order.order_number?.toString() || order.id?.toString() || '';
          const key = `${orderId}_${item.title || item.name}_${variant}`;
          const t = trackMap.get(key);
          return t?.received;
        });
        const hasTracking = !!(order.tracking_number);
        if (allReceived && !hasTracking) readyCount++;
      }
    } else {
      // Storefront
      const eligible = orders.filter((o: any) =>
        !['SHIPPED', 'DELIVERED', 'CANCELLED'].includes(o.status)
      );
      for (const order of eligible) {
        const items = order.order_items || order.items || [];
        if (items.length === 0) continue;
        const allReceived = items.every((item: any) => {
          const variant = item.variant_title || item.selected_variant || '';
          const orderId = order.order_id?.toString() || order.id?.toString() || '';
          const key = `${orderId}_${item.product_name || item.title}_${variant}`;
          const t = trackMap.get(key);
          return t?.received;
        });
        const hasTracking = !!(order.tracking_number);
        if (allReceived && !hasTracking) readyCount++;
      }
    }

    return readyCount;
  }

  // ── Checklist handlers ─────────────────────────────────────────────────

  const toggleTask = (id: string) => {
    setTasks(prev => {
      const next = prev.map(t => t.id === id ? { ...t, done: !t.done } : t);
      saveChecklist(next);
      return next;
    });
  };

  const completedCount = tasks.filter(t => t.done).length;
  const progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      {/* ── 打卡 Time clock ─────────────────────────────────────────────── */}
      <TimeClockCard />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          今日工作
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchReadyToShip}
          disabled={loading}
        >
          {loading ? <CircularProgress size={18} /> : '重新整理'}
        </Button>
      </Box>

      {/* ── 可出貨訂單 Summary ──────────────────────────────────────────── */}
      <Paper sx={{ p: 2.5, mb: 3, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
             onClick={() => navigate('/receiving')}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShippingIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>可出貨訂單</Typography>
          </Box>
          <IconButton size="small"><GoIcon /></IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {/* Total big number */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h2" fontWeight={700} color={counts.total > 0 ? 'warning.main' : 'success.main'}>
                {counts.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {counts.total > 0 ? '筆訂單等待出貨' : '全部出貨完成 ✓'}
              </Typography>
            </Box>

            {/* Per-platform breakdown */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
              <PlatformChip label="蝦皮" count={counts.shopee} color="#EE4D2D" />
              <PlatformChip label="Shopify" count={counts.shopify} color="#96BF48" />
              <PlatformChip label="官網" count={counts.storefront} color="#4c5e8a" />
            </Box>
          </>
        )}
      </Paper>

      {/* ── Daily Checklist ────────────────────────────────────────────── */}
      <Paper sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>待辦事項</Typography>
          <Chip
            label={`${completedCount}/${tasks.length}`}
            color={progress === 100 ? 'success' : 'default'}
            size="small"
          />
        </Box>

        {/* ── 必做 section ── */}
        <Typography variant="subtitle2" color="error.main" fontWeight={600} sx={{ mb: 1 }}>
          必做
        </Typography>

        {tasks.filter(t => t.priority === 'must').map((task) => (
          <TaskRow key={task.id} task={task} onToggle={toggleTask} counts={counts} loading={loading} />
        ))}

        {/* ── 有空可做 section ── */}
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
          有空可做
        </Typography>

        {tasks.filter(t => t.priority === 'optional').map((task) => (
          <TaskRow key={task.id} task={task} onToggle={toggleTask} counts={counts} loading={loading} />
        ))}

        {progress === 100 && (
          <Box sx={{ textAlign: 'center', mt: 2, py: 1 }}>
            <Typography variant="body1" color="success.main" fontWeight={600}>
              今天的工作都完成了！辛苦了！
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────

function TaskRow({ task, onToggle, counts, loading }: {
  task: DailyTask;
  onToggle: (id: string) => void;
  counts: ReadyToShipCounts;
  loading: boolean;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
        bgcolor: task.done ? 'action.hover' : 'background.paper',
        transition: 'background-color 0.2s',
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={task.done}
              onChange={() => onToggle(task.id)}
              icon={<UncheckedIcon />}
              checkedIcon={<CheckCircleIcon />}
              color="success"
            />
          }
          label={
            <Typography
              variant="body1"
              sx={{
                textDecoration: task.done ? 'line-through' : 'none',
                color: task.done ? 'text.disabled' : 'text.primary',
              }}
            >
              {task.label}
              {task.id === 'ship_orders' && !loading && (
                <Chip
                  label={counts.total > 0 ? `${counts.total} 筆` : '0'}
                  size="small"
                  color={counts.total > 0 ? 'warning' : 'success'}
                  sx={{ ml: 1, height: 22 }}
                />
              )}
            </Typography>
          }
          sx={{ width: '100%', m: 0 }}
        />
      </CardContent>
    </Card>
  );
}

// ── 打卡 Time-clock helpers ──────────────────────────────────────────────────
// Taiwan has no DST, so a fixed +08:00 offset is safe and keeps the card correct
// even if the machine's clock is set to another timezone.

const taipeiHm = (d: Date) =>
  d.toLocaleTimeString('zh-TW', {
    timeZone: 'Asia/Taipei', hour: '2-digit', minute: '2-digit', hour12: false,
  });

// YYYY-MM-DD of the Taipei day containing `d`.
const taipeiYmd = (d: Date) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);

// 'MM/DD HH:mm' in Taipei — for a punch that is not from today, where the time
// alone reads as today's (Jen's 10/16 17:12 showed as 「17:12 上班」 on 10/21).
const taipeiMdHm = (d: Date) =>
  `${taipeiYmd(d).slice(5).replace('-', '/')} ${taipeiHm(d)}`;

// 'HH:mm' typed in the 下班 dialog → absolute instant on today's Taipei day.
const taipeiInstantFromHm = (hm: string, base: Date) =>
  new Date(`${taipeiYmd(base)}T${hm}:00+08:00`);

// ── 打卡 Time-clock card ─────────────────────────────────────────────────────
// Compact card at the top of the dashboard: 打卡人員 picker (the warehouse PC is
// shared), live Taipei clock, and one big button that toggles between
// 上班打卡 / 下班打卡 based on the selected person's last punch. 下班打卡 opens a
// dialog so they can set the real leaving time (still have packages to send)
// and leave a 備註 (代墊, 幫忙處理…).
function TimeClockCard() {
  const { currentUser } = useAuth();

  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [selectedId, setSelectedId] = useState<number | ''>('');
  const [status, setStatus] = useState<PunchStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [now, setNow] = useState(new Date());
  const [message, setMessage] = useState<string | null>(null);

  // 下班 dialog state
  const [outOpen, setOutOpen] = useState(false);
  const [outTime, setOutTime] = useState('');
  const [outNote, setOutNote] = useState('');

  // Live Taipei clock.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Staff list for the picker. Defaults to the logged-in account so the common
  // case stays one tap; anyone else picks their own name first.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await timeClockService.getStaff();
        if (cancelled) return;
        setStaff(list);
        setSelectedId(prev => {
          if (prev !== '') return prev;
          const self = list.find(s => s.id === currentUser?.id);
          return self ? self.id : (list[0]?.id ?? '');
        });
      } catch (err) {
        console.error('Failed to fetch staff list:', err);
        // Picker stays empty → punches fall back to the logged-in account.
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const s = await timeClockService.getStatus(selectedId === '' ? undefined : selectedId);
      setStatus(s);
    } catch (err) {
      console.error('Failed to fetch punch status:', err);
    } finally {
      setStatusLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Next punch: 'in' unless the selected person's last punch was an open 'in'
  // that can still be closed (a stale one is left for Magnus to fix).
  const nextType: PunchType = status?.punch_type === 'in' && !status.stale ? 'out' : 'in';
  const selectedName =
    staff.find(s => s.id === selectedId)?.name || currentUser?.name || '';

  const submitPunch = async (type: PunchType, punchedAt?: Date, note?: string) => {
    setPunching(true);
    setMessage(null);
    try {
      const row = await timeClockService.punch({
        type,
        userId: selectedId === '' ? undefined : selectedId,
        punchedAt,
        note,
      });
      const hm = taipeiHm(new Date(row.punched_at));
      setMessage(`${selectedName} ${hm} 已打${type === 'in' ? '上班' : '下班'}卡`);
      await fetchStatus();
      return true;
    } catch (err: any) {
      const apiMessage = err?.response?.data?.message;
      if (err?.response?.status === 409) {
        // Someone already recorded this state — refresh and inform.
        setMessage(apiMessage || '重複打卡');
        await fetchStatus();
      } else {
        setMessage(apiMessage || '打卡失敗，請稍後再試');
      }
      return false;
    } finally {
      setPunching(false);
    }
  };

  const handleMainButton = () => {
    if (nextType === 'in') {
      submitPunch('in');
      return;
    }
    // 下班 → open the dialog prefilled with the current Taipei time.
    setOutTime(taipeiHm(new Date()));
    setOutNote('');
    setOutOpen(true);
  };

  const handleConfirmOut = async () => {
    if (!/^\d{2}:\d{2}$/.test(outTime)) {
      setMessage('下班時間格式錯誤');
      return;
    }
    const at = taipeiInstantFromHm(outTime, new Date());
    if (Number.isNaN(at.getTime())) {
      setMessage('下班時間格式錯誤');
      return;
    }
    const ok = await submitPunch('out', at, outNote.trim());
    if (ok) setOutOpen(false);
  };

  const timeStr = now.toLocaleTimeString('zh-TW', {
    timeZone: 'Asia/Taipei', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const dateStr = now.toLocaleDateString('zh-TW', {
    timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
  });

  return (
    <Paper sx={{ p: 2.5, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <ClockIcon color="primary" />
        <Typography variant="h6" fontWeight={600}>打卡</Typography>
      </Box>

      {staff.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="punch-user-label">打卡人員</InputLabel>
          <Select
            labelId="punch-user-label"
            label="打卡人員"
            value={selectedId}
            onChange={(e) => {
              setSelectedId(e.target.value as number);
              setMessage(null);
            }}
          >
            {staff.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="h3" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {timeStr}
        </Typography>
        <Typography variant="body2" color="text.secondary">{dateStr}</Typography>
        {!statusLoading && status?.punch_type === 'in' && !status.stale && (
          <Typography variant="caption" color="success.main">
            {selectedName} 上班中（{taipeiYmd(new Date(status.punched_at)) === taipeiYmd(new Date())
              ? taipeiHm(new Date(status.punched_at))
              : taipeiMdHm(new Date(status.punched_at))} 上班）
          </Typography>
        )}
        {!statusLoading && status?.punch_type === 'in' && status.stale && (
          <Typography variant="caption" color="warning.main" component="div">
            {selectedName} {taipeiMdHm(new Date(status.punched_at))} 上班後沒打下班卡 —
            請直接打今天的上班卡，並告訴 Magnus 補登那天的下班時間
          </Typography>
        )}
      </Box>

      <Button
        fullWidth
        size="large"
        variant="contained"
        color={nextType === 'in' ? 'primary' : 'warning'}
        onClick={handleMainButton}
        disabled={punching || statusLoading}
        sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 600 }}
      >
        {punching || statusLoading
          ? <CircularProgress size={24} color="inherit" />
          : (nextType === 'in' ? '上班打卡' : '下班打卡')}
      </Button>

      {message && (
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1.5 }}>
          {message}
        </Typography>
      )}

      <Dialog open={outOpen} onClose={() => setOutOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{selectedName} 下班打卡</DialogTitle>
        <DialogContent>
          <TextField
            label="下班時間"
            type="time"
            value={outTime}
            onChange={(e) => setOutTime(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
            helperText="還要寄包裹再走的話，填實際離開的時間"
          />
          <TextField
            label="備註（選填）"
            value={outNote}
            onChange={(e) => setOutNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            placeholder="例：代墊運費 120、留下來寄完包裹"
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutOpen(false)} disabled={punching}>取消</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmOut} disabled={punching}>
            {punching ? <CircularProgress size={20} color="inherit" /> : '確認下班'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

function PlatformChip({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h5" fontWeight={700} sx={{ color }}>
        {count}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default DashboardPage;
